import {config} from "../config";
import {FilterMap, ProductFilter} from "../models/filter";
import App from "../components/app";
import {FilterType, Gender, GenderFromString} from "../models/product";
import {customerService} from "./customer-service";

export class FilterService {
    private filterResponse: any;

    public async initFilterMap(): Promise<FilterMap> {
        const filterParams = this.extractUrlParams();
        const gender: Gender = GenderFromString(filterParams['gender']);
        let filterMap = await this.createFilterMap(gender, filterParams);
        this.updateUrlSearchParams(filterMap);
        return filterMap;
    }

    public async createFilterMap(gender: Gender, params: any = null) {
        let filterMap: FilterMap = new FilterMap(gender, App.countryCode);

        const filterResponse = await this.loadAllFilters(App.countryCode);
        if (filterMap.gender == Gender.Female) {
            if (filterResponse && filterResponse.women) {
                filterMap.applyFilters(filterResponse.women);
            }
        } else if (filterMap.gender == Gender.Male) {
            if (filterResponse && filterResponse.men) {
                filterMap.applyFilters(filterResponse.men);
            }
        } else if (filterMap.gender == Gender.None) {
            if (filterResponse && filterResponse.none) {
                filterMap.applyFilters(filterResponse.none);
            }
        }

        // apply params
        if (params) {
            if (params.custom) {
                filterMap.selectFilter(params.custom, filterMap.custom);
            }
            if (params.likes) {
                if (customerService.isLoggedIn) {
                    filterMap.selectFilter(params.likes, filterMap.likes);
                } else {
                    this.updateUrlSearchParams(filterMap);
                }
            }
            if (params.brand) {
                for (const brand of params.brand) {
                    filterMap.selectFilter(brand, filterMap.brands);
                }
            }
            if (params.web_category) {
                for (const webCat of params.web_category) {
                    filterMap.selectFilter(webCat, filterMap.categories);
                }
            }
            if (params.color) {
                for (const color of params.color) {
                    filterMap.selectFilter(color, filterMap.colors);
                }
            }
            if (params.collections) {
                for (const collection of params.collections) {
                    filterMap.selectFilter(collection, filterMap.collections);
                }
            }
            if (params.editorials) {
                for (const editorial of params.editorials) {
                    filterMap.selectFilter(editorial, filterMap.editorials);
                }
            }
        }

        filterMap.updateSelectedFilters();

        const selectedEditorialCollection = filterMap.collections.find(c => c.type === FilterType.Editorial && c.selected)
        if (selectedEditorialCollection) {
            filterMap.editorials.find(e => e.value === selectedEditorialCollection.value).selected = true;
        }

        return filterMap;
    }

    public updateUrlSearchParams(filterMap: FilterMap): void {
        const params = this.createUrlSearchParams(filterMap);
        history.replaceState(null, null, window.location.pathname + '?' + params);
    }

    private async loadAllFilters(country: string): Promise<any> {
        let url = `${config.backendBasePath}/v2/filters?country=${country}`;
        try {
            if (!this.filterResponse) {
                const response = await fetch(
                    url, {
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        method: 'GET'
                    });

                this.filterResponse = await response.json();
            }
            return this.filterResponse;
        } catch (e) {
            console.warn("error loading filters: ", e);
        }
    };

    // noinspection JSMethodCanBeStatic
    getTranslationKey(filter: ProductFilter, gender: string) {
        if (filter && filter.name) {
            let filterKey = filter.name.trim().toLowerCase();
            filterKey = filterKey.replace(new RegExp(' ', 'g'), '_');
            if (gender.toLowerCase() === 'male' && filter.type === 'CATEGORY') {
                return `filter_haka_${filterKey}`
            } else if (filter.type === 'COLOR') {
                return `filter_color_${filterKey}`
            } else {
                return `filter_${filterKey}`
            }
        }
    }

    public extractUrlParams(): any {
        const params = {};

        try {
            this.getUrlParameter('brand') ? params['brand'] = this.getUrlParameter('brand').split(',') : [];
            this.getUrlParameter('gender') ? params['gender'] = this.getUrlParameter('gender') : {};
            this.getUrlParameter('custom') ? params['custom'] = this.getUrlParameter('custom') : {};
            this.getUrlParameter('likes') ? params['likes'] = this.getUrlParameter('likes') : {};
            this.getUrlParameter('color') ? params['color'] = this.getUrlParameter('color').split(',') : [];
            this.getUrlParameter('web_category') ? params['web_category'] = this.getUrlParameter('web_category').split(',') : [];
            this.getUrlParameter('collections') ? params['collections'] = this.getUrlParameter('collections').split(',') : [];
            this.getUrlParameter('editorials') ? params['editorials'] = this.getUrlParameter('editorials').split(',') : [];
        } catch (e) {
            console.log("extracting filters from url failed: " + e.message);
        }

        return params
    }

    // noinspection JSMethodCanBeStatic
    public getSearchParams(): string {
        const search = new URLSearchParams(window.location.search)
        return search.toString() ? `?${search.toString()}` : ""
    }

    // noinspection JSMethodCanBeStatic
    private getUrlParameter(name) {

        let urlSupported = true;
        let search = window.location.search;

        try {
            (new URLSearchParams(search)).get('web_category')
        } catch (e) {
            urlSupported = false;
        }
        if (urlSupported) {
            return new URLSearchParams(search).get(name)
        } else {
            name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
            let regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
            let results = regex.exec(search);
            return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
        }
    };

    // noinspection JSMethodCanBeStatic
    createUrlSearchParams(filterMap: FilterMap): string {
        let params: string = "";
        params += `gender=${filterMap.gender}`;

        const custom: string[] = [];
        const brand: string[] = [];
        const categories: string[] = [];
        const colors: string[] = [];
        const likes: string[] = [];
        const collections: string[] = [];
        const editorials: string[] = [];

        if (filterMap.selected) {
            for (const filter of filterMap.selected) {
                switch (filter.type) {
                    case FilterType.Custom:
                        custom.push(filter.name);
                        break;
                    case FilterType.Brand:
                        brand.push(filter.name);
                        break;
                    case FilterType.Category:
                        categories.push(filter.name);
                        break;
                    case FilterType.Color:
                        colors.push(filter.name);
                        break;
                    case FilterType.Like:
                        likes.push(filter.name);
                        break;
                    case FilterType.Collection:
                        collections.push(filter.value);
                        break;
                    case FilterType.Editorial:
                        editorials.push(filter.value);
                        break;
                }
            }
        }

        if (custom.length > 0) {
            params += `&custom=${custom.join(",")}`;
        }
        if (brand.length > 0) {
            params += `&brand=${brand.join(",")}`;
        }
        if (categories.length > 0) {
            params += `&web_category=${categories.join(",")}`;
        }
        if (colors.length > 0) {
            params += `&color=${colors.join(",")}`;
        }
        if (likes.length > 0) {
            params += `&likes=${likes.join(",")}`;
        }
        if (collections.length > 0) {
            params += `&collections=${collections.join(",")}`;
        }
        if (editorials.length > 0) {
            params += `&editorials=${editorials.join(",")}`;
        }

        return params;
    }

}

export const filterService = new FilterService();
